import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageHero from '../components/PageHero';

const PrivacyPage: React.FC = () => {
  const { language } = useLanguage();

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white">
      <PageHero 
        title={language === 'en' ? 'Privacy Policy' : 'Adatvédelmi Szabályzat'} 
        subtitle={language === 'en' 
          ? 'How we protect and handle your personal data'
          : 'Hogyan védjük és kezeljük személyes adatait'
        } 
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12 text-center">
            <p className="text-md text-gray-500 italic">
              {language === 'en' ? 'Last updated: ' : 'Utoljára frissítve: '} 
              {new Date().toLocaleDateString()}
            </p>
          </div>
          
          <div className="prose prose-indigo prose-lg text-gray-500 max-w-none">
            {language === 'en' ? (
              <>
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">1. Introduction</h2>
                  <p className="mb-0">
                    Welcome to YouCRM. We respect your privacy and are committed to protecting your personal data. 
                    This privacy policy will inform you how we look after your personal data when you visit our 
                    website and tell you about your privacy rights and how the law protects you.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">2. Data We Collect</h2>
                  <p>
                    We may collect, use, store and transfer different kinds of personal data about you which we 
                    have grouped together as follows:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li>Identity Data: includes first name, last name, title</li>
                    <li>Contact Data: includes email address, telephone numbers, business address</li>
                    <li>Technical Data: includes internet protocol (IP) address, browser type and version, time zone setting</li>
                    <li>Usage Data: includes information about how you use our website and services</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">3. How We Use Your Data</h2>
                  <p>
                    We will only use your personal data when the law allows us to. Most commonly, we will use 
                    your personal data in the following circumstances:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li>To provide you with information about our services</li>
                    <li>To respond to your inquiries</li>
                    <li>To improve our website and services</li>
                    <li>To comply with legal obligations</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">4. Data Security</h2>
                  <p className="mb-0">
                    We have put in place appropriate security measures to prevent your personal data from being 
                    accidentally lost, used, or accessed in an unauthorized way, altered, or disclosed.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">5. Your Legal Rights</h2>
                  <p>
                    Under certain circumstances, you have rights under data protection laws in relation to your personal data, including:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li>The right to access your personal data</li>
                    <li>The right to correction of your personal data</li>
                    <li>The right to erasure of your personal data</li>
                    <li>The right to object to processing of your personal data</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">6. Contact Us</h2>
                  <p className="mb-0">
                    If you have any questions about this privacy policy or our privacy practices, please contact us at:
                    <br />
                    <a href="mailto:info@webforyou.hu" className="text-indigo-600 hover:text-indigo-800 font-medium">
                      info@webforyou.hu
                    </a>
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">1. Bevezetés</h2>
                  <p className="mb-0">
                    Üdvözöljük a YouCRM oldalán. Tiszteletben tartjuk az Ön adatainak védelmét és elkötelezettek 
                    vagyunk személyes adatainak védelme mellett. Ez az adatvédelmi szabályzat tájékoztatja Önt 
                    arról, hogyan kezeljük személyes adatait, amikor weboldalunkat meglátogatja, és tájékoztatja 
                    Önt adatvédelmi jogairól és arról, hogyan védi Önt a törvény.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">2. Az általunk gyűjtött adatok</h2>
                  <p>
                    Különféle személyes adatokat gyűjthetünk, használhatunk, tárolhatunk és továbbíthatunk Önről, 
                    amelyeket a következőképpen csoportosítottunk:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li>Azonosító adatok: vezetéknév, keresztnév, megszólítás</li>
                    <li>Kapcsolattartási adatok: e-mail cím, telefonszámok, üzleti cím</li>
                    <li>Technikai adatok: internet protokoll (IP) cím, böngésző típusa és verziója, időzóna beállítás</li>
                    <li>Használati adatok: információk arról, hogyan használja weboldalunkat és szolgáltatásainkat</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">3. Hogyan használjuk adatait</h2>
                  <p>
                    Személyes adatait csak akkor használjuk fel, ha azt a törvény lehetővé teszi. Leggyakrabban 
                    a következő körülmények között használjuk személyes adatait:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li>Szolgáltatásainkról való tájékoztatás céljából</li>
                    <li>Megkeresésekre való válaszadás céljából</li>
                    <li>Weboldalunk és szolgáltatásaink fejlesztése céljából</li>
                    <li>Jogi kötelezettségek teljesítése céljából</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">4. Adatbiztonság</h2>
                  <p className="mb-0">
                    Megfelelő biztonsági intézkedéseket vezettünk be annak érdekében, hogy megakadályozzuk 
                    személyes adatainak véletlen elvesztését, használatát vagy jogosulatlan hozzáférését, 
                    módosítását vagy közzétételét.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">5. Az Ön jogai</h2>
                  <p>
                    Bizonyos körülmények között az adatvédelmi törvények szerint jogai vannak személyes adataival 
                    kapcsolatban, beleértve:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li>Személyes adataihoz való hozzáférés joga</li>
                    <li>Személyes adatainak helyesbítéséhez való jog</li>
                    <li>Személyes adatainak törléséhez való jog</li>
                    <li>Személyes adatai feldolgozása elleni tiltakozás joga</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">6. Kapcsolat</h2>
                  <p className="mb-0">
                    Ha bármilyen kérdése van ezzel az adatvédelmi szabályzattal vagy adatvédelmi gyakorlatunkkal 
                    kapcsolatban, kérjük, vegye fel velünk a kapcsolatot:
                    <br />
                    <a href="mailto:info@webforyou.hu" className="text-indigo-600 hover:text-indigo-800 font-medium">
                      info@webforyou.hu
                    </a>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage; 